/* eslint-disable class-methods-use-this */
import { BehaviorSubject } from "rxjs";
import StageScene from "../StageScene";
import Track from "../Track";
import getRenderer from "./getRenderer";
import { storeUserSettings, getUserSettings } from "./getDefaultSettings";
import disposeRenderer from "../../../../common/helpers/disposeWebglRenderer";

class Stage {
  constructor({ state }) {
    const settings = getUserSettings();

    this.webgl0 = null;
    this.webgl1 = null;

    this.topScene = null;
    this.mainScene = null;

    this.tracks = [];
    this.elWidth = 1;
    this.elHeight = 1;
    this.tracks = [];

    this.targetEl0 = null;
    this.targetEl1 = null;
    this.targetEl2 = null;

    this.zoomUniforms = {};

    this.variantRegistry = new Map();
    this.state$ = new BehaviorSubject(state || {});
    this.preferences$ = new BehaviorSubject(settings);
    this.regionState$ = new BehaviorSubject({});
    this.highlightedRegion = null;
    this.xFrustum = [0, 1];

    this.createRenderer();
  }

  createRenderer() {
    this.webgl0 = getRenderer({ antialiasingMode: true });
    this.webgl1 = getRenderer({ antialiasingMode: false });

    this.topScene = new StageScene(this.webgl0, this.webgl1);
    this.mainScene = new StageScene(this.webgl0, this.webgl1);

    this.updateSize();
    this.renderAll();
  }

  resetRenderer() {
    this.topScene.dispose();
    this.mainScene.dispose();

    this.createRenderer();
    this.appendToTarget();

    this.topScene = new StageScene(this.webgl0, this.webgl1);
    this.mainScene = new StageScene(this.webgl0, this.webgl1);

    this.topScene.setTracks(this.tracks.filter((x) => x.isStickyTop));
    this.mainScene.setTracks(this.tracks.filter((x) => !x.isStickyTop));

    this.recalcTotalHeight();
    this.updateZoomUniforms();

    this.updateXFrustum();
    this.updateSize();
    this.renderAll();
  }

  setTracks(trackConfs, zoomService) {
    this.topScene.dispose();
    this.mainScene.dispose();

    this.topScene = new StageScene(this.webgl0, this.webgl1);
    this.mainScene = new StageScene(this.webgl0, this.webgl1);
    this.tracks = trackConfs.map((x) => new Track(x, this, zoomService));

    this.topScene.setTracks(this.tracks.filter((x) => x.isStickyTop));
    this.mainScene.setTracks(this.tracks.filter((x) => !x.isStickyTop));
  }

  setPreferences(updatedPrefs) {
    const prefs = { ...this.preferences$.value, ...updatedPrefs };
    this.preferences$.next(prefs);
    storeUserSettings(prefs);
  }

  renderAll() {
    this.topScene.render();
    this.mainScene.render();
  }

  setWidth(width) {
    if (this.elWidth !== width) {
      this.elWidth = width;
      this.updateSize(this.elWidth, this.elHeight);
    }
  }

  setHeight(height) {
    if (this.elHeight !== height && height > 0) {
      this.elHeight = height;
      this.updateSize();
    }
  }

  updateSize() {
    this.topScene.setSize(this.elWidth, this.elHeight, true);
    this.mainScene.setSize(this.elWidth, this.elHeight);
  }

  setTarget(el0, el1, el2) {
    this.targetEl0 = el0;
    this.targetEl1 = el1;
    this.targetEl2 = el2;
    this.appendToTarget();
  }

  appendToTarget() {
    if (
      this.targetEl1 &&
      this.webgl1?.domElement &&
      this.targetEl1.appendChild
    ) {
      this.targetEl0.innerHTML = "";
      this.targetEl1.innerHTML = "";

      this.targetEl0.appendChild(this.webgl0.domElement);
      this.targetEl1.appendChild(this.webgl1.domElement);
    }
  }

  recalcTotalHeight() {
    this.topScene.calcTotalHeight();
    this.mainScene.calcTotalHeight();

    this.topScene.setTopOffset(0);
    this.mainScene.setTopOffset(this.topScene.scrollHeight);

    this.topScene.adjustTrackHeights();
    this.mainScene.adjustTrackHeights();

    this.tracks.forEach((x) => x.updateTrackDimensions(this.tracks));
    return this.mainScene.scrollHeight + this.topScene.scrollHeight;
  }

  setScroll(scrollOffset) {
    this.mainScene.scroll(scrollOffset);
  }

  setXFrustum(...args) {
    this.xFrustum = args;
    this.updateXFrustum();
  }

  updateXFrustum() {
    this.topScene.setXFrustum(this.xFrustum[0], this.xFrustum[1]);
    this.mainScene.setXFrustum(this.xFrustum[0], this.xFrustum[1]);
  }

  setZoomUniforms(u) {
    this.zoomUniforms = u;
    this.topScene.setZoom(u);
    this.mainScene.setZoom(u);
    this.renderAll();
  }

  updateZoomUniforms() {
    this.topScene.setZoom(this.zoomUniforms);
    this.mainScene.setZoom(this.zoomUniforms);
    this.renderAll();
  }

  setState(state) {
    this.state$.next(state);
  }

  setHighlightedArea(region) {
    this.highlightedRegion = region;
    this.tracks.forEach((x) => x.onHighlightedRegionChanged());
    this.tracks.forEach((x) => x.onRegionStateChange());
  }

  setRegionState(x) {
    const { value: current } = this.regionState$;
    const regionState = { ...current, ...x };
    this.regionState$.next(regionState);
    this.tracks.forEach((y) => y.onRegionStateChange());
  }

  destroy() {
    this.tracks.forEach((x) => x.dispose());

    this.mainScene.dispose();
    this.topScene.dispose();

    disposeRenderer(this.webgl0);
    disposeRenderer(this.webgl1);

    this.webgl0 = null;
    this.webgl1 = null;

    this.topScene = null;
    this.mainScene = null;
    this.state$.complete();
    this.preferences$.complete();
    this.variantRegistry = new Map();
  }
}

export default Stage;
